import React, { useEffect, useState } from 'react';
import Icon from '../AppIcon';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ id, type, message, duration = 3000, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(100);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(id), 300);
  };

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    if (isPaused) return;

    const interval = 50;
    const decrement = (interval / duration) * 100;

    const timer = setInterval(() => {
      setProgress(prev => {
        const next = prev - decrement;
        if (next <= 0) {
          handleClose();
          return 0;
        }
        return next;
      });
    }, interval);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaused, duration]);

  const typeConfig: Record<
    ToastType,
    { icon: string; bgColor: string; borderColor: string; iconColor: string; progressColor: string }
  > = {
    success: {
      icon: 'CheckCircle2',
      bgColor: 'bg-white',
      borderColor: 'border-green-200',
      iconColor: 'text-green-600',
      progressColor: 'bg-green-500',
    },
    error: {
      icon: 'XCircle',
      bgColor: 'bg-white',
      borderColor: 'border-red-200',
      iconColor: 'text-red-600',
      progressColor: 'bg-red-500',
    },
    warning: {
      icon: 'AlertTriangle',
      bgColor: 'bg-white',
      borderColor: 'border-amber-200',
      iconColor: 'text-amber-600',
      progressColor: 'bg-amber-500',
    },
    info: {
      icon: 'Info',
      bgColor: 'bg-white',
      borderColor: 'border-blue-200',
      iconColor: 'text-blue-600',
      progressColor: 'bg-blue-500',
    },
  };

  const config = typeConfig[type];

  return (
    <div
      className={`
        transform transition-all duration-300 ease-out
        ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div
        className={`
          ${config.bgColor} ${config.borderColor}
          border rounded-lg shadow-lg overflow-hidden
          min-w-[320px] max-w-[420px]
        `}
      >
        <div className="flex items-start gap-3 p-4">
          <div className="flex-shrink-0 mt-0.5">
            <Icon name={config.icon} size={20} className={config.iconColor} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-800 break-words">{message}</p>
          </div>
          <button
            onClick={handleClose}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            type="button"
          >
            <Icon name="X" size={16} />
          </button>
        </div>
        <div className="h-1 bg-gray-100">
          <div
            className={`h-full ${config.progressColor} transition-all duration-50 ease-linear`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default Toast;
